import type { ReactNode } from 'react';
import { ProfileCard } from '@/components/common/ProfileCard';
import { ProfileFact, ProfileFacts } from '@/components/common/ProfileFacts';
import { ProfileChipList } from '@/components/common/ProfileChip';
import { ProfileMediaRow } from '@/components/common/ProfileMediaRow';
import { Button } from '@/components/ui/Button';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { displayUrl } from '@/lib/utils/format.utils';
import { formatPhoneDisplay } from '@/lib/utils/phone.utils';
import type { PartnerProfileData } from '../../api/partner-profile.api';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

export interface PartnerCardProps {
  data: PartnerProfileData;
  /** The card's Edit button; omitted for read-only viewers (FR-169). */
  action?: ReactNode;
  /** Opens this card's panel from an inline "Add" prompt; omitted when read-only. */
  onAdd?: () => void;
  categoryNameById: Record<string, string>;
  /** Extra content pinned to the card's foot (Delete Profile on Online presence). */
  footer?: ReactNode;
}

const LINK_CLASS = 'text-dkgreen hover:text-dkgreen-hover';

function websiteLink(url: string | undefined) {
  if (!url) return undefined;
  return (
    <a href={url} target="_blank" rel="noreferrer" className={LINK_CLASS}>
      {displayUrl(url)}
    </a>
  );
}

/** About (mockup: 7 of 12 columns): the bio, then contact facts three to a row. */
export function PartnerAboutCard({ data, action }: PartnerCardProps) {
  return (
    <ProfileCard title={S.ABOUT} action={action}>
      {data.description && (
        <p className="text-input text-body-strong mb-5 leading-6">{data.description}</p>
      )}
      <ProfileFacts columns={3}>
        <ProfileFact label={S.FACT_CONTACT} value={data.contactName} />
        <ProfileFact label={S.FACT_TITLE} value={data.contactTitle} />
        <ProfileFact label={S.FACT_PHONE} value={formatPhoneDisplay(data.phone)} />
        <ProfileFact label={S.FACT_LOGIN_EMAIL} value={data.contactEmail} truncate />
        <ProfileFact label={S.FACT_INQUIRY_EMAIL} value={data.inquiryEmail} truncate />
        <ProfileFact label={S.FACT_WEBSITE} truncate>
          {websiteLink(data.websiteUrl)}
        </ProfileFact>
      </ProfileFacts>
    </ProfileCard>
  );
}

const PREVIEW = {
  email: { label: S.CONTACT_BUTTON_EMAIL, target: (d: PartnerProfileData) => d.inquiryEmail },
  scheduling: {
    label: S.CONTACT_BUTTON_SCHEDULING,
    target: (d: PartnerProfileData) => d.schedulingUrl,
  },
} as const;

/**
 * Contact button (mockup: 5 of 12 columns): a preview of the one button
 * members see, chosen by the contact preference (FR-177). Display only; it is
 * edited with the About details.
 */
export function PartnerContactCard({ data, action }: PartnerCardProps) {
  const preview = PREVIEW[data.contactPreference === 'scheduling' ? 'scheduling' : 'email'];
  const target = preview.target(data);
  return (
    <ProfileCard title={S.CONTACT_BUTTON} action={action}>
      <p className="text-subtext mb-3.5 text-sm">{S.CONTACT_BUTTON_INTRO}</p>
      <span className="rounded-button bg-dkgreen flex min-h-12 items-center justify-center text-sm font-medium text-white">
        {preview.label}
      </span>
      {target && (
        <p className="text-subtext mt-3 text-[0.8125rem] break-all">
          {S.CONTACT_BUTTON_OPENS(target)}
        </p>
      )}
    </ProfileCard>
  );
}

/** Services & categories (mockup: 7 of 12 columns): category chips, then services two to a row. */
export function PartnerServicesCard({ data, action, categoryNameById }: PartnerCardProps) {
  const categories = (data.categoryIds ?? []).map((id) => categoryNameById[id]).filter(Boolean);
  const services = data.tags ?? [];
  return (
    <ProfileCard title={S.SERVICES_CARD} action={action}>
      {categories.length > 0 && (
        <div className="mb-[1.125rem]">
          <ProfileChipList items={categories} tone="dark" />
        </div>
      )}
      {services.length > 0 ? (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {services.map((service) => (
            <li
              key={service}
              className="bg-page-bg text-navy rounded-[0.875rem] px-4 py-3.5 text-sm font-medium"
            >
              {service}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-input text-subtext">{S.NO_SERVICES}</p>
      )}
    </ProfileCard>
  );
}

/** The last path segment of a profile URL, e.g. `brightline-legal`. */
function handleOf(url: string): string {
  return url.replace(/\/+$/, '').split('/').pop() || url;
}

function PromoVideoRow({ data, onAdd }: PartnerCardProps) {
  if (data.promoVideoUrl) {
    const watch = (
      <a
        href={data.promoVideoUrl}
        target="_blank"
        rel="noreferrer"
        className={`text-sm font-medium ${LINK_CLASS}`}
      >
        {L.WATCH}
      </a>
    );
    return (
      <ProfileMediaRow title={S.PROMO_VIDEO_LABEL} subtitle={data.promoVideoUrl} action={watch} />
    );
  }
  const add = onAdd ? (
    <Button variant="link-sm" onClick={onAdd}>
      {L.ADD_LINK}
    </Button>
  ) : undefined;
  return (
    <ProfileMediaRow title={S.PROMO_VIDEO_LABEL} subtitle={L.NOT_ADDED_YET} action={add} missing />
  );
}

/** Online presence (mockup: 5 of 12 columns): LinkedIn, the promo video, then Delete Profile. */
export function PartnerOnlineCard(props: PartnerCardProps) {
  const { data, action, footer } = props;
  return (
    <ProfileCard title={S.ONLINE_CARD} action={action} stacked>
      <div className="border-panel-border text-navy flex items-center justify-between gap-3 rounded-2xl border px-4 py-3.5 text-sm">
        <span>{S.LINKEDIN_LABEL}</span>
        {data.linkedinUrl ? (
          <a
            href={data.linkedinUrl}
            target="_blank"
            rel="noreferrer"
            className={`truncate ${LINK_CLASS}`}
          >
            {handleOf(data.linkedinUrl)}
          </a>
        ) : (
          <span className="text-subtext">{L.NOT_PROVIDED}</span>
        )}
      </div>
      <PromoVideoRow {...props} />
      {footer && <div className="mt-auto pt-1">{footer}</div>}
    </ProfileCard>
  );
}
